import React, { Component } from "react";
import {
  createAgentOnce,
  type AgentCreateOnceResponse,
  ApiError,
  validateInviteCode,
} from "../../api";
import { formatAuthError } from "../../auth/formatAuthError";
import { getCompanionSession, getUserProfile, setUserProfile } from "../../auth/session";
import { isMobileUserAgent } from "../../utils/isMobileUserAgent";
import "./Dashboard.scss";

interface DashboardProps {}

interface DashboardState {
  isMobile: boolean;
  agentEmail: string | undefined;
  agentName: string | undefined;
  showWaitlistCard: boolean;
  inviteCodeDraft: string;
  inviteError: string | null;
  inviteSubmitting: boolean;
}

class Dashboard extends Component<DashboardProps, DashboardState> {
  constructor(props: DashboardProps) {
    super(props);
    const profile = getUserProfile();
    this.state = {
      isMobile: isMobileUserAgent(),
      agentEmail: profile?.agent_email,
      agentName: profile?.agent_name,
      showWaitlistCard: Dashboard.readWaitlistFromStorage(),
      inviteCodeDraft: "",
      inviteError: null,
      inviteSubmitting: false,
    };
  }

  private static readWaitlistFromStorage(): boolean {
    try {
      return localStorage.getItem("waitlist") === "true";
    } catch {
      return false;
    }
  }

  componentDidMount(): void {
    void this.maybeCreateAgentOnce();
  }

  private async maybeCreateAgentOnce(): Promise<void> {
    if (Dashboard.readWaitlistFromStorage()) {
      return;
    }

    const cached = getUserProfile();
    if (cached?.agent_email) {
      return;
    }

    const session = getCompanionSession();
    if (!session) {
      return;
    }

    try {
      const resp: AgentCreateOnceResponse = await createAgentOnce({
        xSid: session.sid,
        xEmail: session.email,
      });

      if (resp.status !== "SUCCESS" || !resp.agent_email) {
        return;
      }

      setUserProfile({
        agent_email: resp.agent_email,
        agent_name: resp.agent_name ?? cached?.agent_name,
        name: cached?.name,
        pfp: cached?.pfp,
        email_id: cached?.email_id,
      });
      const updated = getUserProfile();
      this.setState({
        agentEmail: updated?.agent_email,
        agentName: updated?.agent_name,
      });
    } catch (err: unknown) {
      if (err instanceof ApiError) {
        // Swallow - dashboard is usable without agent email mapping.
        return;
      }
    }
  }

  private async submitInvite(): Promise<void> {
    const code = this.state.inviteCodeDraft.trim();
    if (!code) {
      this.setState({ inviteError: "Enter an invite code." });
      return;
    }

    const session = getCompanionSession();
    if (!session) {
      this.setState({ inviteError: "Not signed in." });
      return;
    }

    this.setState({ inviteSubmitting: true, inviteError: null });

    try {
      const resp = await validateInviteCode({
        xSid: session.sid,
        xEmail: session.email,
        invite_code: code,
      });

      if (resp.status !== "SUCCESS") {
        this.setState({
          inviteSubmitting: false,
          inviteError: resp.error ?? "Could not validate invite code.",
        });
        return;
      }

      try {
        localStorage.removeItem("waitlist");
      } catch {
        /* ignore */
      }

      this.setState({ inviteSubmitting: false, showWaitlistCard: false });
      window.location.reload();
    } catch (err: unknown) {
      this.setState({
        inviteSubmitting: false,
        inviteError: formatAuthError(err),
      });
    }
  }

  private renderInviteCard(): React.JSX.Element {
    const waitlistRoot = this.state.isMobile
      ? "dashboard-waitlist dashboard-waitlist_mobile"
      : "dashboard-waitlist";
    return (
      <div className={waitlistRoot}>
        <section className="dashboard-waitlist-cards" aria-label="Invite code">
          <article className="dashboard-waitlist-card">
            <h2 className="dashboard-waitlist-card-title">You're on the Waitlist</h2>
            <div className="dashboard-waitlist-card-subs">
            Your account is on the waitlist. If you have an invite code, enter it to get immediate access.
            </div>
            <form
              className="dashboard-waitlist-form"
              onSubmit={(e) => {
                e.preventDefault();
                void this.submitInvite();
              }}
            >
              <label className="dashboard-waitlist-label" htmlFor="dashboard-invite-code">Invite code</label>
              <input
                id="dashboard-invite-code"
                name="invite_code"
                type="text"
                className="dashboard-waitlist-input"
                autoComplete="off"
                value={this.state.inviteCodeDraft}
                onChange={(e) => {
                  this.setState({
                    inviteCodeDraft: e.target.value.toUpperCase(),
                    inviteError: null,
                  });
                }}
                disabled={this.state.inviteSubmitting}
              />
              <button
                type="submit"
                className="dashboard-waitlist-submit"
                disabled={this.state.inviteSubmitting}
              >
                {this.state.inviteSubmitting ? "Please wait…" : "Submit"}
              </button>
              {this.state.inviteError ? (
                <p className="dashboard-waitlist-error" role="alert">
                  {this.state.inviteError}
                </p>
              ) : null}
            </form>
          </article>
        </section>
      </div>
    );
  }

  private renderCards(): React.JSX.Element {
    return (
      <>
        {this.state.showWaitlistCard ? this.renderInviteCard() : null}
        {this.state.showWaitlistCard ? null : (
          <section className="dashboard-home-cards" aria-label="Quick links">
            <article className="dashboard-home-card">
              <h2 className="dashboard-home-card-title">Meet Your Email Assistant</h2>
              <div className="dashboard-home-card-subs">
                CC this email address on any thread where you need help, and it will assist with scheduling, drafting replies, follow-ups, or anything else related to your email. You can mention its name to give instructions.
              </div>
              <div className="dashboard-home-card-body">
                ✨ Agent name: <span>{this.state.agentName}</span>
              </div>
              <div className="dashboard-home-card-body">
                Agent email ID: <span>{this.state.agentEmail}</span>
              </div>
            </article>
          </section>
        )}
      </>
    );
  }

  private renderDesktop(): React.JSX.Element {
    return (
      <div className="dashboard-home">
        {this.renderCards()}
      </div>
    );
  }

  private renderMobile(): React.JSX.Element {
    return (
      <div className="dashboard-home_mobile">
        <header className="dashboard-home-header">
          <h1 className="dashboard-home-title">Dashboard</h1>
          <p className="dashboard-home-subtitle">
            App root placeholder. Connect your account to see live status (coming soon).
          </p>
        </header>
        {this.renderCards()}
      </div>
    );
  }

  render(): React.JSX.Element {
    return this.state.isMobile ? this.renderMobile() : this.renderDesktop();
  }
}

export default Dashboard;
